/**
 * მეილის ნაკადები — ადმინის სამართავი.
 *
 * გვერდი განზრახ ატარებს ჩართვამდე მიმდევრობას: ჯერ კრედენციალები,
 * მერე ყუთის შემოწმება, მერე სატესტო წერილი, მერე „ვის მიუვიდოდა" და
 * მხოლოდ ბოლოს ჩამრთველი. ასე შემთხვევითი მასობრივი დაგზავნა ვერ ხდება.
 */

import { useEffect, useState } from 'react';
import {
  CheckCircle2,
  KeyRound,
  Mail,
  Play,
  Save,
  Send,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { Alert, Button, Card, Field, Spinner } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import {
  useClearEmailSecret,
  useEmailCredentials,
  useEmailFlows,
  useEmailLogs,
  useRunAbsenceFlow,
  useSaveEmailCredentials,
  useSendTestEmail,
  useUpdateEmailFlow,
  useVerifySender,
  type AbsenceRun,
  type EmailFlow,
} from '../../api/queries';

const EVENT_LABEL: Record<string, string> = {
  password_reset: 'პაროლის დაყენება',
  student_absence: 'გაცდენების შეტყობინება',
};

const STATUS_STYLE: Record<string, string> = {
  SENT: 'text-emerald-700 bg-emerald-50',
  FAILED: 'text-rose-700 bg-rose-50',
  SKIPPED: 'text-ink-mute bg-surface-2',
};

/** მეილის არხი — „ინტერაქციის" გვერდის ერთი ჩანართი */
export function EmailFlowsPanel() {
  const { data: flows, isLoading } = useEmailFlows();

  return (
    <>
      <div className="space-y-6">
        <CredentialsCard />

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner className="size-6" />
          </div>
        ) : (
          flows?.map((flow) => <FlowCard key={flow.id} flow={flow} />)
        )}

        <LogsCard />
      </div>
    </>
  );
}

// ============================================================
// კრედენციალები
// ============================================================

function CredentialsCard() {
  const toast = useToast();
  const { data: status, isLoading } = useEmailCredentials();
  const save = useSaveEmailCredentials();
  const clear = useClearEmailSecret();
  const verify = useVerifySender();

  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [testSender, setTestSender] = useState('');

  useEffect(() => {
    if (!status) return;
    setTenantId(status.tenantId ?? '');
    setClientId(status.clientId ?? '');
  }, [status]);

  async function submit() {
    try {
      await save.mutateAsync({
        tenantId: tenantId.trim() || undefined,
        clientId: clientId.trim() || undefined,
        // ცარიელი ველი = არსებული რჩება; ასე შემთხვევით არ წაიშლება
        clientSecret: secret.trim() || undefined,
      });
      setSecret('');
      toast.success('კრედენციალები შენახულია');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'შენახვა ვერ მოხერხდა');
    }
  }

  const [verifyNote, setVerifyNote] = useState<string | null>(null);

  async function runVerify() {
    setVerifyNote(null);
    try {
      const r = await verify.mutateAsync(testSender.trim());
      const roles = r.grantedRoles.length ? r.grantedRoles.join(', ') : '(არცერთი)';

      if (!r.canSend) {
        // consent არ არის — ეს ნამდვილი პრობლემაა და ასეთადვე უნდა ჩანდეს
        toast.error('Mail.Send მინიჭებული არ არის — admin consent საჭიროა');
      } else if (r.mailboxChecked) {
        toast.success(`ყუთი ხელმისაწვდომია: ${r.displayName ?? r.sender}`);
      } else {
        // ტოკენი მოვიდა, ყუთის წაკითხვა კი დირექტორიის უფლებას საჭიროებს —
        // ეს ჩავარდნა არ არის, ამიტომ წითელი შეტყობინება არ ჯდება
        toast.success('კრედენციალები სწორია, Mail.Send მინიჭებულია');
      }

      setVerifyNote(
        [r.note, `ტოკენში მინიჭებული უფლებები: ${roles}`].filter(Boolean).join(' · '),
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'შემოწმება ვერ მოხერხდა');
    }
  }

  if (isLoading) return null;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="size-4" /> Microsoft Graph — წვდომა
        </span>
      }
    >
      <p className="mb-4 text-[13px] text-ink-mute">
        Tenant და client id საიდუმლო არაა. Client secret ბაზაში დაშიფრულად ინახება და აქ
        აღარასოდეს გამოჩნდება.
      </p>

      {!status?.encryptionReady && (
        <div className="mb-4">
          <Alert kind="error">
            <b>CREDENTIALS_KEY არასწორი ფორმატისაა</b> — უნდა იყოს 32 ბაიტი (64 hex ან base64).
            გაასწორე ან საერთოდ წაშალე <code>.env</code>-იდან და გადატვირთე API.
          </Alert>
        </div>
      )}

      {status?.encryptionReady && status.keySource === 'derived' && (
        <div className="mb-4">
          <Alert kind="info">
            დაშიფვრის გასაღები <code>JWT_ACCESS_SECRET</code>-იდან გამოიყვანება — ცალკე
            კონფიგურაცია არ სჭირდება. თუ <code>JWT_ACCESS_SECRET</code>-ს შეცვლი, აქ ჩაწერილი
            საიდუმლო წაუკითხავი გახდება და ხელახლა უნდა შეიყვანო. ამის ასაცილებლად დაამატე
            ცალკე <code>CREDENTIALS_KEY</code>.
          </Alert>
        </div>
      )}

      {status?.secretSource === 'env' && (
        <div className="mb-4">
          <Alert kind="info">
            საიდუმლო ამჟამად <code>.env</code>-იდან იკითხება. აქ ჩაწერისას ბაზის მნიშვნელობა
            გახდება მთავარი.
          </Alert>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Directory (tenant) ID"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          placeholder="ae14e935-…"
        />
        <Field
          label="Application (client) ID"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder="a8640f75-…"
        />
        <Field
          label="Client secret"
          type="password"
          autoComplete="new-password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder={status?.secretConfigured ? '•••••••• (ჩაწერილია)' : 'ჩასვი Entra-დან'}
          hint={
            status?.secretConfigured
              ? 'ცარიელი ველი ტოვებს არსებულს უცვლელად'
              : 'მნიშვნელობა Entra-ში მხოლოდ ერთხელ ჩანს'
          }
        />
        <div className="flex items-end gap-2">
          <Button onClick={submit} disabled={save.isPending}>
            <Save className="size-4" /> შენახვა
          </Button>
          {status?.secretSource === 'database' && (
            <Button
              variant="ghost"
              onClick={() => void clear.mutateAsync().then(() => toast.success('საიდუმლო წაიშალა'))}
              disabled={clear.isPending}
            >
              საიდუმლოს წაშლა
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 border-t border-line pt-4">
        <p className="mb-2 text-sm text-ink-mute">
          შემოწმება გაგზავნის გარეშე — მოდის თუ არა ტოკენი და ჩანს თუ არა ყუთი:
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <Field
            label="გამგზავნის ყუთი"
            value={testSender}
            onChange={(e) => setTestSender(e.target.value)}
            placeholder="doitinfo@itstep.ge"
            className="min-w-64"
          />
          <Button variant="secondary" onClick={runVerify} disabled={!testSender || verify.isPending}>
            <ShieldCheck className="size-4" /> შემოწმება
          </Button>
        </div>

        {verifyNote && (
          <div className="mt-3">
            <Alert kind="info">{verifyNote}</Alert>
          </div>
        )}
      </div>
    </Card>
  );
}

// ============================================================
// ერთი ნაკადი
// ============================================================

function FlowCard({ flow }: { flow: EmailFlow }) {
  const toast = useToast();
  const update = useUpdateEmailFlow();
  const test = useSendTestEmail();
  const runAbsence = useRunAbsenceFlow();

  const [sender, setSender] = useState(flow.senderEmail);
  const [subject, setSubject] = useState(flow.subjectTemplate);
  const [body, setBody] = useState(flow.bodyTemplate);
  const [schedule, setSchedule] = useState(flow.schedule ?? '');
  const [testTo, setTestTo] = useState('');
  const [run, setRun] = useState<AbsenceRun | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isAbsence = flow.event === 'student_absence';
  const dirty =
    sender !== flow.senderEmail ||
    subject !== flow.subjectTemplate ||
    body !== flow.bodyTemplate ||
    (schedule || null) !== flow.schedule;

  async function save(patch: Partial<EmailFlow>) {
    setErrors({});
    try {
      await update.mutateAsync({ id: flow.id, ...patch });
      toast.success('შენახულია');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.details ?? {});
        if (!err.details) toast.error(err.message);
      } else toast.error('შენახვა ვერ მოხერხდა');
    }
  }

  async function sendTest() {
    try {
      await test.mutateAsync({ id: flow.id, to: testTo.trim() });
      toast.success(`სატესტო წერილი გაიგზავნა: ${testTo}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'გაგზავნა ვერ მოხერხდა');
    }
  }

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Mail className="size-4" /> {EVENT_LABEL[flow.event] ?? flow.event}
        </span>
      }
      action={
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={flow.isActive}
            onChange={(e) => void save({ isActive: e.target.checked })}
            className="size-4 accent-brand"
          />
          <span className={flow.isActive ? 'font-medium text-emerald-700' : 'text-ink-mute'}>
            {flow.isActive ? 'ჩართულია' : 'გამორთულია'}
          </span>
        </label>
      }
    >
      <div className="grid gap-4">
        {flow.description && <p className="text-[13px] text-ink-mute">{flow.description}</p>}

        {flow.event === 'password_reset' && !flow.isActive && (
          <Alert kind="error">
            ეს ნაკადი გამორთულია, ამიტომ <b>„დაგავიწყდა პაროლი?"</b> წერილს არ აგზავნის —
            მომხმარებელი დაინახავს „ბმული გამოგზავნილია", მაგრამ არაფერს მიიღებს.
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="გამგზავნი"
            value={sender}
            error={errors.senderEmail}
            onChange={(e) => setSender(e.target.value)}
          />
          {isAbsence && (
            <Field
              label="განრიგი (cron)"
              value={schedule}
              error={errors.schedule}
              onChange={(e) => setSchedule(e.target.value)}
              hint='მაგ. "0 10 * * 1-5" — სამუშაო დღეებში 10:00'
            />
          )}
        </div>

        <Field
          label="სათაური"
          value={subject}
          error={errors.subjectTemplate}
          onChange={(e) => setSubject(e.target.value)}
        />

        <div>
          <label className="mb-1 block text-sm font-medium">ტექსტი (HTML)</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={7}
            className="w-full rounded-lg border border-line bg-surface px-3 py-2 font-mono text-xs"
          />
          {errors.bodyTemplate && (
            <p className="mt-1 text-xs text-rose-600">{errors.bodyTemplate}</p>
          )}
          <p className="mt-2 text-xs text-ink-mute">
            ხელმისაწვდომი ცვლადები:{' '}
            {flow.availableVariables.map((v) => (
              <code key={v} className="mr-1 rounded bg-surface-2 px-1">{`{{${v}}}`}</code>
            ))}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() =>
              void save({
                senderEmail: sender,
                subjectTemplate: subject,
                bodyTemplate: body,
                schedule: isAbsence ? schedule || null : null,
              })
            }
            disabled={!dirty || update.isPending}
          >
            <Save className="size-4" /> შენახვა
          </Button>

          <div className="ml-auto flex flex-wrap items-end gap-2">
            <Field
              label="სატესტო წერილი"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="shen@itstep.org"
              className="min-w-56"
            />
            <Button variant="secondary" onClick={sendTest} disabled={!testTo || test.isPending}>
              <Send className="size-4" /> გაგზავნა
            </Button>
          </div>
        </div>

        {isAbsence && (
          <div className="rounded-lg border border-line bg-surface-2 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                onClick={() =>
                  void runAbsence.mutateAsync(true).then(setRun).catch(() => toast.error('ვერ შესრულდა'))
                }
                disabled={runAbsence.isPending}
              >
                <Play className="size-4" /> ვის მიუვიდოდა (გაგზავნის გარეშე)
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  void runAbsence
                    .mutateAsync(false)
                    .then((r) => {
                      setRun(r);
                      toast.success(`გაიგზავნა: ${r.sent}, გამოტოვდა: ${r.skipped}`);
                    })
                    .catch(() => toast.error('ვერ შესრულდა'))
                }
                disabled={!flow.isActive || runAbsence.isPending}
              >
                ახლავე გაშვება
              </Button>
            </div>

            {run && (
              <div className="mt-3 text-sm">
                <p className="text-ink-mute">
                  კანდიდატი: <b>{run.candidates}</b>
                  {run.preview ? ' (მხოლოდ ჩვენება — არაფერი გაგზავნილა)' : ` · გაიგზავნა: ${run.sent} · გამოტოვდა: ${run.skipped} · ჩავარდა: ${run.failed}`}
                </p>
                {run.reason && <p className="mt-1 text-amber-700">{run.reason}</p>}
                {run.preview && run.preview.length > 0 && (
                  <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                    {run.preview.map((p) => (
                      <li key={p.email} className="text-xs">
                        {p.name} — {p.email} · {p.pct ?? '—'}% · ზედიზედ {p.streak}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// ============================================================
// ისტორია
// ============================================================

function LogsCard() {
  const { data, isLoading } = useEmailLogs();

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <CheckCircle2 className="size-4" /> გაგზავნის ისტორია
        </span>
      }
    >
      {data?.schedules && data.schedules.length > 0 && (
        <p className="mb-3 text-xs text-ink-mute">
          აქტიური განრიგი: {data.schedules.join(', ')}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5" />
        </div>
      ) : !data?.logs.length ? (
        <p className="py-6 text-center text-sm text-ink-mute">ჯერ არაფერი გაგზავნილა</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-ink-mute">
              <tr>
                <th className="pb-2">თარიღი</th>
                <th className="pb-2">მოვლენა</th>
                <th className="pb-2">მიმღები</th>
                <th className="pb-2">სტატუსი</th>
              </tr>
            </thead>
            <tbody>
              {data.logs.map((log) => (
                <tr key={log.id} className="border-t border-line">
                  <td className="py-2 whitespace-nowrap text-xs">
                    {new Date(log.createdAt).toLocaleString('ka-GE')}
                  </td>
                  <td className="py-2 text-xs">{EVENT_LABEL[log.event] ?? log.event}</td>
                  <td className="py-2 text-xs">{log.recipient}</td>
                  <td className="py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[log.status] ?? ''}`}
                      title={log.error ?? undefined}
                    >
                      {log.status === 'SENT' ? 'გაიგზავნა' : log.status === 'FAILED' ? 'ჩავარდა' : 'გამოტოვდა'}
                    </span>
                    {log.error && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs text-ink-mute">
                        <TriangleAlert className="size-3" />
                        {log.error.slice(0, 60)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
